import React, { useState, useEffect, useCallback } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { logError } from "@/lib/logger";
import { rangeParams } from "@/lib/dateRanges";
import Dashboard from "@/components/Dashboard";
import CrmInventory from "@/components/CrmInventory";
import TabLoadError from "@/components/shared/TabLoadError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  UserRound,
  TrendingUp,
  Filter,
  ListChecks,
  HeartPulse,
  Activity,
  ArrowRight,
  Loader2,
} from "lucide-react";

// Compact metric row for an overview card.
function Metric({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm py-1 border-b border-border/50 last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

function OverviewCard({ icon: Icon, title, description, metrics, onOpen, testId, loading }) {
  return (
    <Card className="flex flex-col" data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon className="w-4 h-4 text-primary" />
          </span>
          {title}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">{description}</p>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col">
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
            </div>
          ) : (
            metrics.map((m) => <Metric key={m.label} label={m.label} value={m.value} />)
          )}
        </div>
        <Button variant="outline" size="sm" className="mt-4 w-full" onClick={onOpen} data-testid={`${testId}-view`}>
          View
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default function AnalyticsOverview({ range, onRangeChange, rangeLoading, theme, analytics, onOpen }) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [deals, setDeals] = useState(null);
  const [health, setHealth] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [team, setTeam] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const { start, end } = rangeParams(range);
      // ONE request returns all overview sections (cached server-side) instead
      // of 5 separate heavy round-trips.
      const ov = await api.analyticsOverview(start, end);
      setForecast(ov.forecast || null);
      setDeals(ov.deals || null);
      setHealth(ov.data_health || null);
      setFunnel(ov.funnel || null);
      setTeam(ov.team || null);
    } catch (e) {
      logError("Failed to load analytics overview:", e);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  // Derived headline metrics per section.
  const dealRows = deals?.deals || [];
  const openCount = dealRows.filter((r) => r.status === "open").length;
  const wonCount = dealRows.filter((r) => r.status === "won").length;
  const lostCount = dealRows.filter((r) => r.status === "lost").length;
  const topRisk = dealRows
    .filter((r) => r.status === "open" && r.risk != null)
    .sort((a, b) => (b.risk || 0) - (a.risk || 0))[0];

  const primaryFunnel =
    (funnel?.pipelines || []).find((p) => p.pipeline_id === funnel?.primary_pipeline_id) ||
    (funnel?.pipelines || [])[0] ||
    null;

  const act = analytics?.activity_analysis || {};
  const teamTotals = team?.totals || {};

  const cards = [
    {
      key: "team",
      icon: UserRound,
      title: "Reps / Team",
      description: "Per-rep scorecard: wins, win rate, cycle time and activity.",
      testId: "overview-card-team",
      metrics: [
        { label: "Reps", value: fmtNumber(teamTotals.reps || 0) },
        { label: "Team win rate", value: `${teamTotals.win_rate ?? 0}%` },
        { label: "Won value", value: fmtCurrency(teamTotals.won_value) },
      ],
    },
    {
      key: "activity",
      icon: Activity,
      title: "Activity Insights",
      description: "Rep activity mix and trends (calls, emails, meetings, tasks, notes).",
      testId: "overview-card-activity",
      metrics: [
        { label: "Total activities", value: fmtNumber(act?.total || 0) },
        {
          label: "Top type",
          value:
            (act?.by_type || []).slice().sort((a, b) => (b.count || 0) - (a.count || 0))[0]?.type
              ? `${(act.by_type.slice().sort((a, b) => (b.count || 0) - (a.count || 0))[0].type)}`
              : "—",
        },
        { label: "Active reps", value: fmtNumber((act?.by_rep || []).length) },
      ],
    },
    {
      key: "funnel",
      icon: Filter,
      title: "Pipeline",
      description: "Stage-to-stage conversion, drop-off and velocity across your pipeline.",
      testId: "overview-card-funnel",
      metrics: primaryFunnel
        ? [
            { label: "Pipeline", value: primaryFunnel.pipeline_label },
            { label: "Deals", value: fmtNumber(primaryFunnel.deal_count) },
            { label: "Overall conversion", value: `${primaryFunnel.overall_conversion}%` },
          ]
        : [{ label: "Status", value: "Needs HubSpot pipeline data" }],
    },
    {
      key: "forecast",
      icon: TrendingUp,
      title: "Forecast",
      description: "Forward-looking open-pipeline forecast, weighted by win probability.",
      testId: "overview-card-forecast",
      metrics: [
        { label: "Open pipeline", value: fmtCurrency(forecast?.open_pipeline_value) },
        { label: "Weighted forecast", value: fmtCurrency(forecast?.weighted_forecast) },
        { label: "Open deals", value: fmtNumber(forecast?.open_deals || 0) },
      ],
    },
    {
      key: "deals",
      icon: ListChecks,
      title: "Deals",
      description: "Your full book of deals with a risk score on every open deal.",
      testId: "overview-card-deals",
      metrics: [
        { label: "Total deals", value: fmtNumber(deals?.total || 0) },
        { label: "Open / Won / Lost", value: `${openCount} / ${wonCount} / ${lostCount}` },
        { label: "Highest risk", value: topRisk ? `${topRisk.name} (${topRisk.risk})` : "—" },
      ],
    },
    {
      key: "data_health",
      icon: HeartPulse,
      title: "Data Health",
      description: "CRM hygiene scorecard: missing fields, unassigned, overdue, stale.",
      testId: "overview-card-data_health",
      metrics: [
        { label: "Health score", value: health ? `${health.score}/100` : "—" },
        { label: "Deals checked", value: fmtNumber(health?.deal_count || 0) },
        {
          label: "High-severity issues",
          value: fmtNumber(
            (health?.deal_issues || []).filter((i) => i.severity === "high" && i.count > 0).length +
              (health?.contact_issues || []).filter((i) => i.severity === "high" && i.count > 0).length
          ),
        },
      ],
    },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-overview">
      {loadError && (
        <TabLoadError onRetry={load} message="We couldn't load the analytics overview. The request may have timed out — please retry." />
      )}
      <div className="flex items-center gap-2">
        <div>
          <h2 className="font-semibold text-foreground leading-tight">Analytics Overview</h2>
          <p className="text-xs text-muted-foreground">
            A snapshot across every analytics view. Open any section for the full deep-dive.
          </p>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => (
          <OverviewCard
            key={c.key}
            icon={c.icon}
            title={c.title}
            description={c.description}
            metrics={c.metrics}
            loading={loading}
            testId={c.testId}
            onOpen={() => onOpen(c.key)}
          />
        ))}
      </div>

      {/* Scope-aware CRM object inventory (compact) */}
      <CrmInventory compact onOpen={() => onOpen("crm_inventory")} />

      {/* Consolidated Dashboard modules (KPIs, charts, rep performance, risk) */}
      {analytics && (
        <div className="pt-2 border-t border-border" data-testid="overview-dashboard">
          <div className="mb-4">
            <h2 className="font-semibold text-foreground leading-tight">Performance Dashboard</h2>
            <p className="text-xs text-muted-foreground">
              KPIs, forecast, pipeline movement, rep performance and risk — for the selected period.
            </p>
          </div>
          <Dashboard
            data={analytics}
            theme={theme}
            range={range}
            onRangeChange={onRangeChange}
            rangeLoading={rangeLoading}
          />
        </div>
      )}
    </div>
  );
}
